import './FailReportShow.scss';

import { CheckCircleOutlined, CloseCircleOutlined, StopOutlined } from '@ant-design/icons';
import { DateField, RefreshButton, Show, TextField } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { useMutation } from '@tanstack/react-query';
import { Button, Tag } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import FailReportStatusTag from '@/components/failReport/FailReportStatusTag';
import ConfirmModal from '@/components/general/ConfirmModal';
import LinkTextField from '@/components/general/LinkTextField';
import { ShowInfoGrid, ShowInfoItem } from '@/components/general/ShowInfo/ShowInfo';
import { ShowSection } from '@/components/general/ShowInfo/ShowSection';
import {
  FAIL_REPORT_STATUS,
  FailReport,
  FailReportWarrantyString,
} from '@/model/FailReport/FailReport';
import { FailReportItem } from '@/model/FailReport/FailReportItem';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

import FailReportItems from './FailReportItems/FailReportItems';

const FailReportShow = () => {
  const { t } = useTranslation();
  const [failReportItems, setFailReportItems] = useState<FailReportItem[]>([]);
  const { failReportRepository } = useRepositoriesContext();
  const {
    result: failReport,
    query: { isLoading, refetch },
  } = useShow<FailReport>();
  /** Mutación para cambiar el estado del informe de problema */
  const { mutate: changeStatus, isPending: isChangingStatus } = useMutation({
    mutationFn: async (newStatus: FAIL_REPORT_STATUS) => {
      return failReportRepository.updateFailReportStatus(failReport!.id!, newStatus);
    },
    onSuccess: () => {
      void refetch();
    },
  });
  /** Verifica si el informe permite transiciones de estado (no está ya finalizado) */
  const canChangeStatus =
    failReport &&
    ![FAIL_REPORT_STATUS.RESOLVED, FAIL_REPORT_STATUS.CLOSED, FAIL_REPORT_STATUS.CANCELED].includes(
      failReport.status,
    );

  return (
    <Show
      isLoading={isLoading}
      headerButtons={({ refreshButtonProps }) => (
        <>
          <RefreshButton {...refreshButtonProps} onClick={() => void refetch()} hideText />
          {canChangeStatus && (
            <>
              {/* Botón Cancelar */}
              <ConfirmModal
                openButton={
                  <Button icon={<StopOutlined />} disabled={isChangingStatus}>
                    {t('Cancelar')}
                  </Button>
                }
                title={t('¿Cancelar el informe de problema?')}
                message={t(
                  'El informe de problema pasará a estado cancelado. Esta acción no se puede deshacer.',
                )}
                onOk={() => changeStatus(FAIL_REPORT_STATUS.CANCELED)}
              />
              {/* Botón Cerrar */}
              <ConfirmModal
                openButton={
                  <Button icon={<CloseCircleOutlined />} disabled={isChangingStatus}>
                    {t('Cerrar')}
                  </Button>
                }
                title={t('¿Cerrar el informe de problema?')}
                message={t(
                  'El informe de problema pasará a estado cerrado. Esta acción no se puede deshacer.',
                )}
                onOk={() => changeStatus(FAIL_REPORT_STATUS.CLOSED)}
              />
              {/* Botón Resolver */}
              <ConfirmModal
                openButton={
                  <Button type="primary" icon={<CheckCircleOutlined />} disabled={isChangingStatus}>
                    {t('Resolver')}
                  </Button>
                }
                title={t('¿Resolver el informe de problema?')}
                message={t(
                  'El informe de problema pasará a estado resuelto. Esta acción no se puede deshacer.',
                )}
                onOk={() => changeStatus(FAIL_REPORT_STATUS.RESOLVED)}
              />
            </>
          )}
        </>
      )}>
      {failReport && (
        <div className="FailReportShow">
          <div className="FailReportShowHeader">
            <div className="FailReportShowData">
              <b className="FailReportShowDataId">
                {t('Informe de problema')} #{failReport.id}
              </b>
              <span className="FailReportShowHeaderTags">
                <FailReportStatusTag failReportStatus={failReport.status} />
                {failReport.created_automatically && <Tag color="geekblue">{t('Automático')}</Tag>}
              </span>
            </div>
            <div className="FailReportShowData">
              <div>
                <b>{t('Fecha creación')}: </b>
                {dayjs(failReport?.report_date).format('L HH:mm')}
              </div>
              <div>
                <b>{t('Creado por')}: </b>
                {(failReport.created_by as any)
                  ? `${(failReport.created_by as any).first_name} ${(failReport.created_by as any).last_name}`
                  : '-'}
              </div>
            </div>
          </div>

          {/* Sección de detalle */}
          <ShowSection title={t('Detalle del reporte')}>
            <ShowInfoGrid>
              <ShowInfoItem label={t('Ticket')}>
                {failReport.ticket ? (
                  <LinkTextField
                    to={`/${RESOURCES.TICKETS}/show/${failReport.ticket}`}
                    label={`${failReport.ticket}`}
                    local
                  />
                ) : null}
              </ShowInfoItem>
              <ShowInfoItem label={t('Categoría')}>
                <TextField
                  value={
                    !failReport.category
                      ? t('No categorizado')
                      : typeof failReport.category === 'object'
                        ? failReport.category?.title
                        : failReport.category
                  }
                />
              </ShowInfoItem>
              <ShowInfoItem label={t('Subcategoría')}>
                <TextField
                  value={
                    !failReport.subcategory
                      ? t('No categorizado')
                      : typeof failReport.subcategory === 'object'
                        ? failReport.subcategory?.title
                        : failReport.subcategory
                  }
                />
              </ShowInfoItem>
              <ShowInfoItem label={t('Garantía')}>
                <TextField
                  value={
                    failReport.warranty != null
                      ? t(FailReportWarrantyString[failReport.warranty])
                      : '-'
                  }
                />
              </ShowInfoItem>
              {failReport.report_ticket && (
                <ShowInfoItem label={t('Ticket de reporte')}>
                  <LinkTextField
                    to={`/${RESOURCES.TICKETS}/show/${failReport.report_ticket}`}
                    label={`${failReport.report_ticket}`}
                    local
                  />
                </ShowInfoItem>
              )}
              {failReport.review_by && (
                <ShowInfoItem label={t('Revisado por')}>
                  <TextField value={String(failReport.review_by)} />
                </ShowInfoItem>
              )}
              {failReport.review_date && (
                <ShowInfoItem label={t('Fecha de revisión')}>
                  <DateField value={failReport.review_date} format="L" />
                </ShowInfoItem>
              )}
              <ShowInfoItem label={t('Comentarios')} colSpan={2}>
                <span className="FailReportShowPreWrap">{failReport.comment ?? '-'}</span>
              </ShowInfoItem>
            </ShowInfoGrid>
          </ShowSection>

          {/* Items del informe de problema */}
          <FailReportItems
            failReport={failReport.id}
            failReportItems={failReportItems}
            setFailReportItems={setFailReportItems}
          />
        </div>
      )}
    </Show>
  );
};

export default FailReportShow;
